import type { QuotePricingModel, QuotePricingRegion } from "../modules/shared/model/admin-quote-pricing.interfaces";

/**
 * Interface for job data needed for quote pricing calculation
 */
interface JobData {
  categoryId?: string | { id: string };
  province?: string;
  municipality?: string;
  city?: string;
  serviceArea?: { id: string; name: string };
  isEmergency?: boolean;
  isQuote?: boolean;
}

/**
 * Interface for quote price result
 */
export interface QuotePriceResult {
  price: number;
  source: "quote_pricing" | "fallback";
  matchedRegion?: QuotePricingRegion;
  commissionRate?: number;
}

/**
 * Extract category ID from job data
 */
const getCategoryId = (categoryId: string | { id: string } | undefined): string | null => {
  if (!categoryId) return null;
  if (typeof categoryId === "string") return categoryId;
  return categoryId.id || null;
};

/**
 * Find matching quote pricing region for a job
 * 
 * Priority matching:
 * 1. ServiceArea ID match
 * 2. Region name match
 * 3. Province/state match
 * 4. Municipality match
 * 5. City match
 */
export const findMatchingQuotePricingRegion = (
  quotePricings: QuotePricingModel[],
  jobData: JobData
): QuotePricingRegion | null => {
  if (!quotePricings || quotePricings.length === 0) {
    return null;
  }

  const jobCategoryId = getCategoryId(jobData.categoryId);
  
  if (!jobCategoryId) {
    console.log("No category ID found in job data");
    return null;
  }

  console.log("Finding quote pricing match for:", {
    categoryId: jobCategoryId,
    province: jobData.province,
    municipality: jobData.municipality,
    city: jobData.city,
    serviceArea: jobData.serviceArea,
    isEmergency: jobData.isEmergency
  });

  // Find quote pricing that matches the job's category
  const matchingPricing = quotePricings.find(pricing => {
    const pricingCategoryId = getCategoryId(pricing?.category?.id as any);
    return pricingCategoryId === jobCategoryId;
  });

  if (!matchingPricing || !matchingPricing.regions || matchingPricing.regions.length === 0) {
    console.log("No matching pricing found for category:", jobCategoryId);
    return null;
  }

  console.log("Found matching pricing with", matchingPricing.regions.length, "regions");

  // Try to match region based on serviceArea or location data
  let matchingRegion: QuotePricingRegion | null = null;

  // Extract region identifiers from job (with safe string conversion)
  const jobRegionId = jobData.serviceArea?.id;
  const jobRegionName = typeof jobData.serviceArea?.name === "string" 
    ? jobData.serviceArea.name.toLowerCase() 
    : null;
  const jobProvince = typeof jobData.province === "string" 
    ? jobData.province.toLowerCase() 
    : null;
  const jobCity = typeof jobData.city === "string" 
    ? jobData.city.toLowerCase() 
    : null;
  const jobMunicipality = typeof jobData.municipality === "string" 
    ? jobData.municipality.toLowerCase() 
    : null;

  // Find the best matching region
  for (const region of matchingPricing.regions) {
    const regionId = region?.region?.id;
    const regionName = typeof region?.region?.name === "string" 
      ? region.region.name.toLowerCase() 
      : null;
    
    // Handle region.region.state - it might be a string or undefined
    const regionState = region?.region?.state && typeof region.region.state === "string"
      ? region.region.state.toLowerCase()
      : null;
    
    // Handle region.region.city
    const regionCity = region?.region?.city && typeof region.region.city === "string"
      ? region.region.city.toLowerCase()
      : null;

    console.log("Checking region:", {
      regionId,
      regionName,
      regionState,
      regionCity,
      subcategory: region?.subcategory
    });

    // Priority 1: Match by serviceArea ID
    if (jobRegionId && regionId && jobRegionId === regionId) {
      console.log("✅ Match by serviceArea ID");
      matchingRegion = region;
      break;
    }

    // Priority 2: Match by region name
    if (jobRegionName && regionName && jobRegionName === regionName) {
      console.log("✅ Match by region name");
      matchingRegion = region;
      break;
    }

    // Priority 3: Match by province/state
    if (jobProvince && regionState && jobProvince === regionState) {
      console.log("✅ Match by province/state");
      matchingRegion = region;
      // Don't break yet, continue looking for more specific matches
    }

    // Priority 4: Match by region name with province
    if (jobProvince && regionName && jobProvince === regionName) {
      console.log("✅ Match by province with region name");
      if (!matchingRegion) {
        matchingRegion = region;
      }
    }

    // Priority 5: Match by municipality
    if (jobMunicipality && regionName && jobMunicipality.includes(regionName)) {
      console.log("✅ Match by municipality");
      if (!matchingRegion) {
        matchingRegion = region;
      }
    }

    // Priority 6: Match by city
    if (jobCity && (regionCity === jobCity || regionName === jobCity)) {
      console.log("✅ Match by city");
      if (!matchingRegion) {
        matchingRegion = region;
      }
    }
  }

  if (matchingRegion) {
    console.log("✅ Final matching region found:", matchingRegion.region?.name);
  } else {
    console.log("❌ No matching region found");
  }

  return matchingRegion;
};

/**
 * Calculate quote price for a job based on quote pricing rules
 * Falls back to provided fallback price if no match found
 */
export const calculateQuotePrice = (
  quotePricings: QuotePricingModel[],
  jobData: JobData,
  fallbackPrice: number = 20
): QuotePriceResult => {
  const matchingRegion = findMatchingQuotePricingRegion(quotePricings, jobData);

  if (matchingRegion) {
    console.log("Using quote pricing from matched region");

    let price = fallbackPrice;
    const commissionRate = matchingRegion.regularCommission || 0;

    // Determine price based on job type
    if (jobData.isQuote && jobData.isEmergency) {
      // Emergency quote
      price = matchingRegion.emergencyQuotePrice || matchingRegion.baseQuotePrice || fallbackPrice;
    } else if (jobData.isQuote && !jobData.isEmergency) {
      // Regular quote
      price = matchingRegion.baseQuotePrice || fallbackPrice;
    } else {
      // Direct hiring (not quote)
      price = matchingRegion.baseQuotePrice || fallbackPrice;
    }

    return {
      price,
      source: "quote_pricing",
      matchedRegion: matchingRegion,
      commissionRate
    };
  }

  console.log("No quote pricing match found, using fallback price:", fallbackPrice);
  return {
    price: fallbackPrice,
    source: "fallback"
  };
};

/**
 * Get commission rate for a job based on quote pricing rules
 * Returns null if no match found
 */
export const getQuotePricingCommission = (
  quotePricings: QuotePricingModel[],
  jobData: JobData
): number | null => {
  const matchingRegion = findMatchingQuotePricingRegion(quotePricings, jobData);

  if (!matchingRegion) {
    return null;
  }

  // Return appropriate commission based on modifiers
  // You can extend this to check time of day, holidays, etc.
  return matchingRegion.regularCommission || null;
};

/**
 * Check if a job matches any quote pricing rules
 */
export const hasQuotePricingMatch = (
  quotePricings: QuotePricingModel[],
  jobData: JobData
): boolean => {
  return findMatchingQuotePricingRegion(quotePricings, jobData) !== null;
};

/**
 * Get detailed pricing information for a job
 */
export interface DetailedQuotePricing {
  basePrice: number;
  emergencyPrice: number;
  commissions: {
    regular: number;
    day?: number;
    night?: number;
    holiday?: number;
  };
  modifiers: {
    distance: boolean;
    night: boolean;
    holiday: boolean;
    emergency: boolean;
  };
  region: {
    id: string;
    name: string;
    country?: string;
  };
  source: "quote_pricing" | "fallback";
}

export const getDetailedQuotePricing = (
  quotePricings: QuotePricingModel[],
  jobData: JobData,
  fallbackPrice: number = 20
): DetailedQuotePricing | null => {
  const matchingRegion = findMatchingQuotePricingRegion(quotePricings, jobData);

  if (!matchingRegion || !matchingRegion.region) {
    return null;
  }

  return {
    basePrice: matchingRegion.baseQuotePrice || fallbackPrice,
    emergencyPrice: matchingRegion.emergencyQuotePrice || fallbackPrice,
    commissions: {
      regular: matchingRegion.regularCommission || 0,
      day: matchingRegion.regularDayCommission,
      night: matchingRegion.regularNightCommission,
      holiday: matchingRegion.regularHolidayCommission
    },
    modifiers: {
      distance: matchingRegion.distanceModifier || false,
      night: matchingRegion.nightModifier || false,
      holiday: matchingRegion.holidayModifier || false,
      emergency: matchingRegion.emergencyModifier || false
    },
    region: {
      id: matchingRegion.region.id,
      name: matchingRegion.region.name || "Unknown",
      country: typeof matchingRegion.region.country === "object" 
        ? matchingRegion.region.country.name 
        : matchingRegion.region.country
    },
    source: "quote_pricing"
  };
};

